import { useCallback } from 'react';
import { type DragEndEvent, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

type UseActionOrderDndProps = {
  actionOrder: string[];
  onReorderActions: (actionIds: string[]) => void;
};

type UseActionOrderDndReturnType = {
  actionSensors: ReturnType<typeof useSensors>;
  handleActionDragEnd: (event: DragEndEvent) => void;
};

export const useActionOrderDnd = ({ actionOrder, onReorderActions }: UseActionOrderDndProps): UseActionOrderDndReturnType => {
  const actionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleActionDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = actionOrder.findIndex((actionId) => actionId === active.id);
      const newIndex = actionOrder.findIndex((actionId) => actionId === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      onReorderActions(arrayMove(actionOrder, oldIndex, newIndex));
    },
    [actionOrder, onReorderActions]
  );

  return {
    actionSensors,
    handleActionDragEnd,
  };
};
